import { TASK_ACTION_COPY } from '../../task-action/task-action-copy';

/**
 * The six lost reasons (MYDAY_LEAD_COMMS_PLAN.md Appendix I.5, FE-136; PR22): the design's fixed list (Trovve.dc.html
 * handoff 5, D5:8900 `LOST_REASONS`), in its order. A reason is always optional (`Skip reason`) and is stored as its
 * CODE on the Lost transition row (`TrClientStageTransitions.LostReason`, nvarchar(32)); the label is display only.
 * The design's `more-horizontal` icon is lucide's `ellipsis` (the same glyph, renamed by lucide).
 *
 * Used by `vf-lost-reason-pills` on every surface that asks why a lead was lost: the rail's `Lead outcome` popover
 * (FE-138), the task panel's Edit-relationship popover (FE-140) and Lead Next Steps (FE-141, P4).
 */

/** A stored lost-reason code (BE-66 accepts exactly these). */
export type LostReasonCode =
    'chose_another_firm' | 'no_response' | 'fees_too_high' | 'not_a_fit' | 'not_ready_yet' | 'other';

/** One lost reason: the stored code, the pill's label and its lucide icon name. */
export interface LostReason {
    readonly code: LostReasonCode;
    readonly label: string;
    readonly icon: string;
}

/** The column the code is stored in is nvarchar(32) (DB-07). */
export const LOST_REASON_CODE_MAX_LENGTH = 32;

/** I.5, in order. Labels verbatim from the design. */
export const LOST_REASONS: readonly LostReason[] = [
    { code: 'chose_another_firm', label: 'Chose another firm', icon: 'building' },
    { code: 'no_response', label: 'No response', icon: 'message-circle' },
    { code: 'fees_too_high', label: 'Fees too high', icon: 'circle-dollar-sign' },
    { code: 'not_a_fit', label: 'Not a fit', icon: 'user-x' },
    { code: 'not_ready_yet', label: 'Not ready yet', icon: 'hourglass' },
    { code: 'other', label: 'Other', icon: 'ellipsis' },
];

/** One of the six codes, exactly (case and spacing included); anything else is not a reason. */
export function isLostReasonCode(value: unknown): value is LostReasonCode {
    return typeof value === 'string' && LOST_REASONS.some((reason) => reason.code === value);
}

/**
 * The hint beside `Why?` (I.3, PR24): `Optional — helps show why leads drop off.` until a reason is picked, then
 * `Reason saved.` (the design's wording without the AI's name).
 */
export function lostReasonHint(picked: string | null | undefined): string {
    return isLostReasonCode(picked) ? TASK_ACTION_COPY.LOST_WHY.hintPicked : TASK_ACTION_COPY.LOST_WHY.hintIdle;
}
